import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsService } from '@ddt-web/analytics';
import { ERROR_SEVERITY, ErrorDCR, PAErrorDCR } from '@ddt-web/config';
import { InjectQueryParam } from '@ddt-web/generic-utils';
import { PortfolioIdUtil } from '@ddt-web/portfolio-data';
import { PaQueryParamKey, SharedQueryParamKey } from '@ddt-web/url';
import * as get from 'lodash/get';
import * as toPairsIn from 'lodash/toPairsIn';

import { PasErrorPortfolioDetails, PasSecurityDetailsPartialError } from '../../models';
import { ErrorObject } from '../../models/error-object';
import { PopupDialogAction, PopupDialogObject } from '../../models/popup-dialog-object';
import { ErrorHandlerUtil } from '../../utils';
import { WweErrorUtil } from '../../utils/wwe-error.util';
import { AbstractErrorHandlerService } from '../abstract-error-handler.service';
import { ErrorHelperService } from '../error-helper.service';
import { ErrorPopupService } from '../error-popup.service';

/**
 * This error handler is to be used when on asset resolve failure, we want to display the first failed portfolio, for which there has been partial failure.
 */

@Injectable()
export class PasSecurityDetailsErrorHandlerService extends AbstractErrorHandlerService {
	@InjectQueryParam(SharedQueryParamKey.PORTFOLIOS) private readonly portfolios: string;
	@InjectQueryParam(SharedQueryParamKey.ENGAGEMENT) private readonly engagement: string;
	@InjectQueryParam(PaQueryParamKey.ROUTED_FROM) private readonly routedFrom: string;

	private flagErrorConfigs = ['positionDetails'];

	constructor(
		protected contentService: ContentService,
		protected errorPopupService: ErrorPopupService,
		protected tealiumAnalyticsService: TealiumAnalyticsService,
		protected errorHelperService: ErrorHelperService
	) {
		super(contentService, errorPopupService, tealiumAnalyticsService, errorHelperService);
	}

	public handleError(error): void {
		const configName = get(error, 'configName', '');
		if (error instanceof ErrorObject && this.flagErrorConfigs.includes(configName)) {
			// Setup scope variables
			const errorObject = error.responseJson;

			const isPartialError = errorObject instanceof PasSecurityDetailsPartialError;

			// Set error severity
			this.errorSeverity = ERROR_SEVERITY.CRITICAL;
			this.httpRequestId = error?.responseJson?.headers?.get('x-request-id');

			let dialogErrorMessage: PopupDialogObject;

			if (isPartialError) {
				const errorCodes = errorObject.errorCodes;
				const errorCodesList = toPairsIn(errorCodes);
				const securityErrorMessage = this.contentService.get('SECURITY_DETAILS_COMMON_ERROR', ErrorDCR.SECURITY_DETAILS_COMMON_ERROR);

				// Log each error to console
				errorCodesList.forEach(error => WweErrorUtil.logSecurityErrorsToConsole(error[0], error[1], configName, securityErrorMessage));

				if (errorObject?.errorPortfolios?.[0]?.id === 'LIST_OF_BENCHMARKS') {
					console.error('There was an error while retrieving benchmark data for config: ' + configName, errorObject);

					// Build default error object
					dialogErrorMessage = this.handleDialogErrorMessage();
				} else {
					// Build partial error dialog
					dialogErrorMessage = this.handlePartialErrorDialogErrorMessage(get(errorObject, 'errorPortfolios', []));
				}
			} else {
				console.error('There was an error while retrieving data for config: ' + configName, errorObject);

				// Build default error object
				dialogErrorMessage = this.handleDialogErrorMessage();
			}
			this.handleErrorTracking('unabletoprocessrequest', this.errorSeverity);
			dialogErrorMessage.trackMsgPrefix = this.errorSeverity;
			this.errorPopupService.openResponseMessageDialog(dialogErrorMessage, true);
		} else {
			this.nextHandler.handleError(error);
		}
	}

	private handlePartialErrorDialogErrorMessage(errorPortfolios: PasErrorPortfolioDetails[]): PopupDialogObject {
		const [description, actions] = this.getDialogDetailsByViewCategory(errorPortfolios);

		const dialogErrorMessage = this.errorHelperService.buildPopupDialogObject(description, null, false, this.buildErrorMessage());
		WweErrorUtil.configurePopupDisplayForSecurityError(errorPortfolios, dialogErrorMessage);

		dialogErrorMessage.actions = actions;
		return dialogErrorMessage;
	}

	private handleDialogErrorMessage(): PopupDialogObject {
		const description = this.contentService.get('SECURITY_DETAILS_COMMON_ERROR', ErrorDCR.SECURITY_DETAILS_COMMON_ERROR);
		const label = this.contentService.get('PA_RETRY_MESSAGE', PAErrorDCR.PA_RETRY_LABEL);

		return this.errorHelperService.buildPopupDialogObject(description, label, true, this.buildErrorMessage());
	}

	private buildViewPortfolioInEditorAction(firstFailedPortfolioId: string): PopupDialogAction {
		return WweErrorUtil.buildViewPortfolioInEditorAction(
			firstFailedPortfolioId,
			this.contentService.get('ERROR_REVIEW_PORTFOLIO_HOLDINGS_LABEL', ErrorDCR.ERROR_REVIEW_PORTFOLIO_HOLDINGS_LABEL)
		);
	}

	private getDialogDetailsByViewCategory(errorPortfolios: PasErrorPortfolioDetails[]): [string, PopupDialogAction[]] {
		let description = '';
		let actions = [];
		if (this.engagement) {
			if (this.routedFrom === 'apb') {
				description = this.contentService.get('MISSING_SECURITY_DETAILS_MESSAGE_APB', ErrorDCR.MISSING_SECURITY_DETAILS_MESSAGE_APB);
				actions = [
					PopupDialogAction.create({
						label: this.contentService.get('ERROR_VIEW_APB_LABEL', ErrorDCR.ERROR_VIEW_APB_LABEL),
						type: 'resolve',
						unresolvedUrl: 'apbPage',
						reload: false
					})
				];
			} else {
				description = this.contentService.get('MISSING_SECURITY_DETAILS_MESSAGE_ENGAGEMENT', ErrorDCR.MISSING_SECURITY_DETAILS_MESSAGE_ENGAGEMENT);
				actions = [
					PopupDialogAction.create({
						label: this.contentService.get('ERROR_VIEW_DASHBOARD_LABEL', ErrorDCR.ERROR_VIEW_DASHBOARD_LABEL),
						type: 'resolve',
						unresolvedUrl: 'dashboardUrl',
						reload: false
					})
				];
			}
		} else {
			const portfolioIds = this.portfolios
				.split(',')
				.filter(item => item !== 'list');
			const errorPortfolio = errorPortfolios?.[0]?.id || portfolioIds?.[0];
			if (PortfolioIdUtil.isModel(errorPortfolio)) {
				description = this.contentService.get(
					'SECURITY_DETAILS_HOLDING_ERROR_DESCRIPTION_WITHOUT_REVIEW',
					ErrorDCR.SECURITY_DETAILS_HOLDING_ERROR_DESCRIPTION_WITHOUT_REVIEW
				);
				actions = [
					ErrorHandlerUtil.buildPopupAction(
						this.contentService.get('ERROR_NEW_ANALYSIS_LABEL', ErrorDCR.ERROR_NEW_ANALYSIS_LABEL),
						'view_new_analysis',
						false
					)
				];
			}
			else {
				description = this.contentService.get(
					'SECURITY_DETAILS_HOLDING_ERROR_DESCRIPTION',
					ErrorDCR.SECURITY_DETAILS_HOLDING_ERROR_DESCRIPTION
				);
				actions = [
					ErrorHandlerUtil.buildPopupAction(
						this.contentService.get('ERROR_NEW_ANALYSIS_LABEL', ErrorDCR.ERROR_NEW_ANALYSIS_LABEL),
						'view_new_analysis',
						false,
						'btn-secondary'
					),
					this.buildViewPortfolioInEditorAction(errorPortfolio)
				];
			}
		}

		return [description, actions];
	}
}
